/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Longform Article Wizard State Store (Task 10.2.3 & 10.2.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type ArticleLength = 'short' | 'medium' | 'long' | 'deep';

export interface LengthConfig {
  id: ArticleLength;
  label: string;
  approxWords: number;
  description: string;
  badge?: string;
}

export const ARTICLE_LENGTH_OPTIONS: LengthConfig[] = [
  {
    id: 'short',
    label: 'Short Form',
    approxWords: 800,
    description: 'Quick briefing, concise analysis or social article',
  },
  {
    id: 'medium',
    label: 'SEO Standard',
    approxWords: 1500,
    description: 'Optimal word count for ranking in standard Google searches',
    badge: 'Popular',
  },
  {
    id: 'long',
    label: 'Comprehensive Guide',
    approxWords: 3000,
    description: 'Pillar page with multi-tier sub-headings and deep analysis',
  },
  {
    id: 'deep',
    label: 'Technical Monograph',
    approxWords: 5000,
    description: 'In-depth research whitepaper, report or mini-ebook',
  },
];

export interface BrandVoiceOption {
  id: string;
  name: string;
  tone: string;
  description: string;
  isDefault?: boolean;
}

export const DEFAULT_BRAND_VOICES: BrandVoiceOption[] = [
  {
    id: 'bv_tech_expert',
    name: 'Tech Specialist',
    tone: 'authoritative',
    description: 'Concise, packed with architectural evidence, direct without filler.',
    isDefault: true,
  },
  {
    id: 'bv_visionary_founder',
    name: 'Visionary Founder',
    tone: 'inspirational',
    description: 'Strategic, inspirational, distilling founder lessons.',
  },
  {
    id: 'bv_practical_friendly',
    name: 'Approachable & Practical',
    tone: 'conversational',
    description: 'Conversational, step-by-step clarity with intuitive examples.',
  },
  {
    id: 'bv_academic_analyst',
    name: 'Academic & Analytical',
    tone: 'academic',
    description: 'Rigorous analytical structure, objective arguments and cross-referenced data.',
  },
  {
    id: 'bv_persuasive_sales',
    name: 'Persuasive & Conversion',
    tone: 'persuasive',
    description: 'Customer pain-point focused, highlighting core value and decisive CTA.',
  },
];

export interface SeoTitleOption {
  id: string;
  title: string;
  seoScore: number;
  angle: string;
  characterCount: number;
  rationale: string;
}

export interface ArticleHeading {
  id: string;
  title: string;
  level: 2 | 3;
  keyPoints?: string[];
  estimatedWords?: number;
}

export interface WizardSetupState {
  topic: string;
  primaryKeyword: string;
  secondaryKeywords: string[];
  targetLength: ArticleLength;
  language: string;
  brandVoiceId: string;
  customTone?: string;
}

export interface GenerationProgressState {
  isGenerating: boolean;
  isPaused: boolean;
  progressPercent: number;
  currentSectionIndex: number;
  totalSections: number;
  currentHeadingTitle: string;
  generatedSections: Record<string, string>; // headingId -> markdown content
  fullArticleMarkdown: string;
  totalWords: number;
  totalWordsGenerated?: number;
  statusMessage: string;
  error: string | null;
}

export interface LongformWizardStore {
  // Navigation & Step Tracking (1 = Setup, 2 = Titles, 3 = Outline, 4 = Stream/Review)
  currentStep: 1 | 2 | 3 | 4;
  completedSteps: number[];

  // Step 1: Setup parameters
  setup: WizardSetupState;

  // Step 2: SEO Titles
  generatedTitles: SeoTitleOption[];
  selectedTitleId: string | null;
  customTitle: string;
  isGeneratingTitles: boolean;

  // Step 3: Outlines Tree (Task 10.2.4)
  headings: ArticleHeading[];
  isGeneratingOutlines: boolean;

  // Step 4: Generation State (Task 10.2.4)
  generation: GenerationProgressState;

  // Actions
  setStep: (step: 1 | 2 | 3 | 4) => void;
  markStepCompleted: (step: number) => void;
  updateSetup: (updates: Partial<WizardSetupState>) => void;
  addSecondaryKeyword: (keyword: string) => boolean;
  removeSecondaryKeyword: (keyword: string) => void;

  // Step 2 actions
  setGeneratedTitles: (titles: SeoTitleOption[]) => void;
  selectTitle: (id: string) => void;
  setCustomTitle: (title: string) => void;
  generateTitles: () => void;
  getSelectedFinalTitle: () => string;

  // Step 3 actions (Outline)
  setHeadings: (headings: ArticleHeading[]) => void;
  addHeading: (title: string, level?: 2 | 3, afterId?: string) => void;
  updateHeading: (id: string, updates: Partial<ArticleHeading>) => void;
  deleteHeading: (id: string) => void;
  reorderHeadings: (newHeadings: ArticleHeading[]) => void;
  enrichSection: (headingId: string) => void;
  generateOutlines: () => void;

  // Step 4 actions (Generation)
  updateGenerationState: (updates: Partial<GenerationProgressState>) => void;
  setSectionContent: (headingId: string, content: string) => void;
  appendSectionDelta: (headingId: string, delta: string) => void;
  finalizeFullArticle: (markdown: string, words: number) => void;
  resetGenerationState: () => void;

  // Global reset
  resetWizard: () => void;
}

/**
 * Calculates a reliable SEO quality score (0 - 100) for a given title.
 */
export function calculateTitleSeoScore(title: string, primaryKeyword: string): number {
  if (!title.trim()) return 0;

  let score = 50; // Base score
  const cleanTitle = title.trim();
  const cleanKeyword = primaryKeyword.trim().toLowerCase();
  const length = cleanTitle.length;

  // 1. Length scoring (Google SERP ideal: 45 - 65 chars)
  if (length >= 45 && length <= 65) {
    score += 25;
  } else if (length >= 35 && length <= 75) {
    score += 15;
  } else if (length < 25 || length > 85) {
    score -= 15;
  }

  // 2. Primary keyword presence
  if (cleanKeyword && cleanTitle.toLowerCase().includes(cleanKeyword)) {
    score += 15;
    // Bonus if keyword is in the first half of the title
    if (cleanTitle.toLowerCase().indexOf(cleanKeyword) <= 20) {
      score += 5;
    }
  }

  // 3. Numbers presence (Listicles & Guides: +5)
  if (/\d+/.test(cleanTitle)) {
    score += 5;
  }

  // 4. Power words detection (+5)
  const powerWords = [
    'bí quyết', 'hướng dẫn', 'toàn diện', 'chiến lược', 'thực chiến',
    'đột phá', 'xu hướng', 'cẩm nang', 'sai lầm', 'tối ưu', 'làm chủ',
    'guide', 'best', 'proven', 'secret', 'master', 'strategy', 'complete'
  ];
  if (powerWords.some((word) => cleanTitle.toLowerCase().includes(word))) {
    score += 5;
  }

  // Clamp score between 10 and 100
  return Math.max(10, Math.min(100, Math.round(score)));
}

/**
 * Generates 5 distinct SEO title candidates with calculated scores & rationales.
 */
export function generateSeoTitlesAlgorithm(
  topic: string,
  primaryKeyword: string,
  language: string
): SeoTitleOption[] {
  const kw = primaryKeyword.trim() || topic.trim() || 'Công Nghệ AI';
  const currentYear = 2026;

  let templates: Array<{ title: string; angle: string; rationale: string }>;

  if (language === 'en') {
    templates = [
      {
        title: `${kw} Complete Guide: Proven Strategies for ${currentYear}`,
        angle: 'Comprehensive Guide',
        rationale: 'High CTR format with current year anchor and actionable keyword positioning.',
      },
      {
        title: `How to Master ${kw}: 7 Game-Changing Steps You Need to Know`,
        angle: 'Actionable Steps',
        rationale: 'Number-driven title that sparks curiosity while keeping keywords prominent.',
      },
      {
        title: `The Ultimate Blueprint to ${kw} in ${currentYear} (Case Study)`,
        angle: 'Case Study & Blueprint',
        rationale: 'Authority-focused wording demonstrating concrete results and social proof.',
      },
      {
        title: `5 Common ${kw} Mistakes to Avoid and What to Do Instead`,
        angle: 'Mistakes & Pitfalls',
        rationale: 'Loss-aversion trigger consistently generating strong search click-throughs.',
      },
      {
        title: `${kw} Deep Dive: Expert Insights and Future Trends`,
        angle: 'Expert Analysis',
        rationale: 'Thought leadership positioning tailored for executive and technical audiences.',
      },
    ];
  } else {
    // Default Vietnamese
    templates = [
      {
        title: `Hướng Dẫn Toàn Diện Về ${kw} Từ A-Z Mới Nhất ${currentYear}`,
        angle: 'Cẩm Nang Toàn Diện',
        rationale: 'Định dạng cẩm nang toàn diện chuẩn Google SERP, kích thích tỷ lệ nhấp chuột (CTR).',
      },
      {
        title: `Bí Quyết ${kw}: 7 Chiến Lược Thực Chiến Đột Phá Cho Doanh Nghiệp`,
        angle: 'Chiến Lược Thực Chiến',
        rationale: 'Chứa con số lẻ ấn tượng (7) và từ khóa hành động giúp tăng 32% CTR so với tiêu đề thường.',
      },
      {
        title: `Làm Chủ ${kw} Năm ${currentYear}: Xu Hướng & Phương Pháp Tối Ưu`,
        angle: 'Đón Đầu Xu Hướng',
        rationale: 'Tạo cảm giác cấp thiết và tính thời điểm với mốc thời gian năm hiện tại.',
      },
      {
        title: `5 Sai Lầm Phổ Biến Khi Triển Khai ${kw} Và Giải Pháp Khắc Phục`,
        angle: 'Tránh Bẫy Sai Lầm',
        rationale: 'Khai thác tâm lý phòng ngừa rủi ro của người đọc, cực kỳ hiệu quả trong SEO On-page.',
      },
      {
        title: `Nghiên Cứu Chuyên Sâu: Tối Ưu Hiệu Quả ${kw} Đạt Đỉnh Cao`,
        angle: 'Chuyên Khảo Phân Tích',
        rationale: 'Phong cách chuyên gia đáng tin cậy, phù hợp với độc giả tìm kiếm giải pháp có chiều sâu.',
      },
    ];
  }

  return templates.map((tmpl, index) => {
    const score = calculateTitleSeoScore(tmpl.title, kw);
    return {
      id: `seo_title_${index + 1}_${Date.now()}`,
      title: tmpl.title,
      seoScore: score,
      angle: tmpl.angle,
      characterCount: tmpl.title.length,
      rationale: tmpl.rationale,
    };
  });
}

/**
 * Generates an initial structured outline tree (H2 & H3) based on topic, length and keywords.
 */
export function generateOutlinesAlgorithm(
  _title: string,
  primaryKeyword: string,
  targetLength: ArticleLength,
  language: string
): ArticleHeading[] {
  const kw = primaryKeyword.trim() || 'Công nghệ';
  const targetWords = ARTICLE_LENGTH_OPTIONS.find((o) => o.id === targetLength)?.approxWords || 1500;

  if (language === 'en') {
    const baseHeadings: ArticleHeading[] = [
      {
        id: 'h2_intro',
        title: `Introduction: Why ${kw} Matters in 2026`,
        level: 2,
        keyPoints: ['Current landscape overview', 'Core challenges solved by this approach'],
        estimatedWords: Math.round(targetWords * 0.12),
      },
      {
        id: 'h3_intro_trends',
        title: `Key Drivers Accelerating Adoption of ${kw}`,
        level: 3,
        keyPoints: ['Performance benchmarks', 'Cost efficiency gains'],
        estimatedWords: Math.round(targetWords * 0.08),
      },
      {
        id: 'h2_core_concepts',
        title: `Fundamental Architecture & Core Principles of ${kw}`,
        level: 2,
        keyPoints: ['Underlying mechanisms', 'Key structural components'],
        estimatedWords: Math.round(targetWords * 0.2),
      },
      {
        id: 'h3_arch_breakdown',
        title: `Technical Deep Dive: How the Engine Operates`,
        level: 3,
        keyPoints: ['Data flow', 'Event-driven lifecycle'],
        estimatedWords: Math.round(targetWords * 0.1),
      },
      {
        id: 'h2_implementation',
        title: `Step-by-Step Implementation Guide for Production`,
        level: 2,
        keyPoints: ['Prerequisites and setup', 'Hands-on workflow and configuration'],
        estimatedWords: Math.round(targetWords * 0.25),
      },
      {
        id: 'h3_step_code',
        title: `Configuration and Execution Walkthrough`,
        level: 3,
        keyPoints: ['Practical code examples', 'Environment setup'],
        estimatedWords: Math.round(targetWords * 0.1),
      },
      {
        id: 'h2_pitfalls',
        title: `Common Mistakes to Avoid and Optimization Tips`,
        level: 2,
        keyPoints: ['Critical pitfalls', 'Best practices for scalability'],
        estimatedWords: Math.round(targetWords * 0.1),
      },
      {
        id: 'h2_conclusion',
        title: `Conclusion and Future Outlook: Actionable Next Steps`,
        level: 2,
        keyPoints: ['Summary of takeaways', 'Recommended action plan'],
        estimatedWords: Math.round(targetWords * 0.05),
      },
    ];
    return baseHeadings;
  }

  // Default Vietnamese
  const baseHeadings: ArticleHeading[] = [
    {
      id: 'h2_intro',
      title: `1. Tổng Quan & Bối Cảnh: Vì Sao ${kw} Trở Thành Xu Hướng Đột Phá 2026`,
      level: 2,
      keyPoints: ['Bối cảnh thị trường hiện tại', 'Những bài toán cấp bách mà giải pháp này tháo gỡ'],
      estimatedWords: Math.round(targetWords * 0.12),
    },
    {
      id: 'h3_intro_importance',
      title: `Lợi ích chiến lược của ${kw} đối với doanh nghiệp`,
      level: 3,
      keyPoints: ['Tăng tốc độ xử lý', 'Tối ưu hóa ngân sách vận hành'],
      estimatedWords: Math.round(targetWords * 0.08),
    },
    {
      id: 'h2_architecture',
      title: `2. Nguyên Lý Cốt Lõi & Kiến Trúc Nền Tảng Của ${kw}`,
      level: 2,
      keyPoints: ['Các thành phần cấu tạo chính', 'Quy trình luân chuyển dữ liệu và xử lý nghiệp vụ'],
      estimatedWords: Math.round(targetWords * 0.2),
    },
    {
      id: 'h3_arch_details',
      title: `Phân tích sâu cơ chế hoạt động thực tế`,
      level: 3,
      keyPoints: ['Độ trễ phản hồi thấp', 'Khả năng chịu tải linh hoạt'],
      estimatedWords: Math.round(targetWords * 0.1),
    },
    {
      id: 'h2_step_guide',
      title: `3. Hướng Dẫn Từng Bước Triển Khai Thực Chiến Chuẩn Quốc Tế`,
      level: 2,
      keyPoints: ['Các bước chuẩn bị tiền đề', 'Thiết lập cấu hình và vận hành thực tế'],
      estimatedWords: Math.round(targetWords * 0.25),
    },
    {
      id: 'h3_guide_configs',
      title: `Mẹo thiết lập tham số tối ưu và thực hành tốt nhất`,
      level: 3,
      keyPoints: ['Ví dụ trực quan', 'Quy chuẩn an toàn bảo mật'],
      estimatedWords: Math.round(targetWords * 0.1),
    },
    {
      id: 'h2_mistakes',
      title: `4. Các Sai Lầm Phổ Biến Thường Gặp Và Phương Pháp Khắc Phục`,
      level: 2,
      keyPoints: ['Bẫy cấu hình sai', 'Giải pháp khắc phục triệt để'],
      estimatedWords: Math.round(targetWords * 0.1),
    },
    {
      id: 'h2_conclusion',
      title: `5. Tổng Kết & Lời Khuyên Hành Động: Lộ Trình Bứt Phá Thành Công`,
      level: 2,
      keyPoints: ['Đúc kết các điểm mấu chốt', 'Kế hoạch hành động 30 ngày tới'],
      estimatedWords: Math.round(targetWords * 0.05),
    },
  ];

  return baseHeadings;
}

const INITIAL_SETUP: WizardSetupState = {
  topic: '',
  primaryKeyword: '',
  secondaryKeywords: [],
  targetLength: 'medium',
  language: 'vi',
  brandVoiceId: 'bv_tech_expert',
  customTone: '',
};

const INITIAL_GENERATION: GenerationProgressState = {
  isGenerating: false,
  isPaused: false,
  progressPercent: 0,
  currentSectionIndex: 0,
  totalSections: 0,
  currentHeadingTitle: '',
  generatedSections: {},
  fullArticleMarkdown: '',
  totalWords: 0,
  statusMessage: 'Sẵn sàng khởi động đường ống sinh bài',
  error: null,
};

export const useLongformWizardStore = create<LongformWizardStore>()(
  persist(
    (set, get) => ({
      currentStep: 1,
      completedSteps: [],
      setup: INITIAL_SETUP,
      generatedTitles: [],
      selectedTitleId: null,
      customTitle: '',
      isGeneratingTitles: false,
      headings: [],
      isGeneratingOutlines: false,
      generation: INITIAL_GENERATION,

      setStep: (step) => set({ currentStep: step }),

      markStepCompleted: (step) =>
        set((state) => ({
          completedSteps: Array.from(new Set([...state.completedSteps, step])),
        })),

      updateSetup: (updates) =>
        set((state) => ({
          setup: { ...state.setup, ...updates },
        })),

      addSecondaryKeyword: (keyword: string) => {
        const clean = keyword.trim();
        if (!clean) return false;
        const current = get().setup.secondaryKeywords;
        if (current.some((k) => k.toLowerCase() === clean.toLowerCase())) {
          return false;
        }
        if (current.length >= 8) {
          return false;
        }
        set((state) => ({
          setup: {
            ...state.setup,
            secondaryKeywords: [...state.setup.secondaryKeywords, clean],
          },
        }));
        return true;
      },

      removeSecondaryKeyword: (keyword: string) => {
        set((state) => ({
          setup: {
            ...state.setup,
            secondaryKeywords: state.setup.secondaryKeywords.filter((k) => k !== keyword),
          },
        }));
      },

      setGeneratedTitles: (titles) => {
        set({
          generatedTitles: titles,
          selectedTitleId: titles.length > 0 ? titles[0].id : null,
          customTitle: titles.length > 0 ? titles[0].title : '',
        });
      },

      selectTitle: (id) => {
        const found = get().generatedTitles.find((t) => t.id === id);
        set({
          selectedTitleId: id,
          customTitle: found ? found.title : get().customTitle,
        });
      },

      setCustomTitle: (title) => {
        set({ customTitle: title });
      },

      generateTitles: () => {
        const { topic, primaryKeyword, language } = get().setup;
        set({ isGeneratingTitles: true });
        try {
          const titles = generateSeoTitlesAlgorithm(topic, primaryKeyword, language);
          get().setGeneratedTitles(titles);
          get().markStepCompleted(1);
        } finally {
          set({ isGeneratingTitles: false });
        }
      },

      getSelectedFinalTitle: () => {
        const { customTitle, selectedTitleId, generatedTitles } = get();
        if (customTitle.trim()) {
          return customTitle.trim();
        }
        const selected = generatedTitles.find((t) => t.id === selectedTitleId);
        return selected ? selected.title : '';
      },

      // Step 3 Outlines Actions
      setHeadings: (headings) => set({ headings }),

      addHeading: (title, level = 2, afterId) => {
        const newHeading: ArticleHeading = {
          id: `heading_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
          title: title.trim() || 'Mục Mới',
          level,
          keyPoints: [],
          estimatedWords: level === 2 ? 300 : 150,
        };

        const current = get().headings;
        if (!afterId) {
          set({ headings: [...current, newHeading] });
          return;
        }

        const index = current.findIndex((h) => h.id === afterId);
        if (index === -1) {
          set({ headings: [...current, newHeading] });
        } else {
          const next = [...current];
          next.splice(index + 1, 0, newHeading);
          set({ headings: next });
        }
      },

      updateHeading: (id, updates) => {
        set((state) => ({
          headings: state.headings.map((h) => (h.id === id ? { ...h, ...updates } : h)),
        }));
      },

      deleteHeading: (id) => {
        set((state) => ({
          headings: state.headings.filter((h) => h.id !== id),
        }));
      },

      reorderHeadings: (newHeadings) => {
        set({ headings: newHeadings });
      },

      enrichSection: (headingId) => {
        const current = get().headings;
        const targetIndex = current.findIndex((h) => h.id === headingId);
        if (targetIndex === -1) return;

        const targetHeading = current[targetIndex]!;
        const sub1: ArticleHeading = {
          id: `heading_sub1_${Date.now()}`,
          title: `Phân tích chuyên sâu: ${targetHeading.title}`,
          level: 3,
          keyPoints: ['Các yếu tố quyết định', 'Dữ liệu thực nghiệm'],
          estimatedWords: 150,
        };
        const sub2: ArticleHeading = {
          id: `heading_sub2_${Date.now()}`,
          title: `Ví dụ ứng dụng và mẹo thực tế`,
          level: 3,
          keyPoints: ['Case study điển hình', 'Các lưu ý quan trọng'],
          estimatedWords: 150,
        };

        const next = [...current];
        next.splice(targetIndex + 1, 0, sub1, sub2);
        set({ headings: next });
      },

      generateOutlines: () => {
        const title = get().getSelectedFinalTitle();
        const { primaryKeyword, targetLength, language } = get().setup;

        set({ isGeneratingOutlines: true });
        try {
          const generated = generateOutlinesAlgorithm(title, primaryKeyword, targetLength, language);
          set({ headings: generated });
          get().markStepCompleted(2);
        } finally {
          set({ isGeneratingOutlines: false });
        }
      },

      // Step 4 Generation Actions
      updateGenerationState: (updates) => {
        set((state) => ({
          generation: { ...state.generation, ...updates },
        }));
      },

      setSectionContent: (headingId, content) => {
        set((state) => ({
          generation: {
            ...state.generation,
            generatedSections: {
              ...state.generation.generatedSections,
              [headingId]: content,
            },
          },
        }));
      },

      appendSectionDelta: (headingId, delta) => {
        set((state) => {
          const prev = state.generation.generatedSections[headingId] || '';
          return {
            generation: {
              ...state.generation,
              generatedSections: {
                ...state.generation.generatedSections,
                [headingId]: prev + delta,
              },
            },
          };
        });
      },

      finalizeFullArticle: (markdown, words) => {
        set((state) => ({
          generation: {
            ...state.generation,
            isGenerating: false,
            isPaused: false,
            progressPercent: 100,
            fullArticleMarkdown: markdown,
            totalWords: words,
            statusMessage: 'Bài viết đã hoàn thành 100%!',
          },
        }));
        get().markStepCompleted(3);
        get().markStepCompleted(4);
      },

      resetGenerationState: () => {
        set({ generation: INITIAL_GENERATION });
      },

      resetWizard: () => {
        set({
          currentStep: 1,
          completedSteps: [],
          setup: INITIAL_SETUP,
          generatedTitles: [],
          selectedTitleId: null,
          customTitle: '',
          isGeneratingTitles: false,
          headings: [],
          isGeneratingOutlines: false,
          generation: INITIAL_GENERATION,
        });
      },
    }),
    {
      name: 'sophia_longform_wizard_state_v1',
      storage: createJSONStorage(() => {
        if (typeof window !== 'undefined' && window.localStorage) {
          return window.localStorage;
        }
        return {
          getItem: () => null,
          setItem: () => {},
          removeItem: () => {},
        };
      }),
      partialize: (state) => ({
        currentStep: state.currentStep,
        completedSteps: state.completedSteps,
        setup: state.setup,
        generatedTitles: state.generatedTitles,
        selectedTitleId: state.selectedTitleId,
        customTitle: state.customTitle,
        headings: state.headings,
      }),
    }
  )
);
